import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, FormField, FormLabel, FormInput, FormSelect, FormTextarea, FormCheckbox } from '../ui';
import type { CampaignFollow } from '../../types/campaignFollow';
import { campaignService } from '../../services/campaign.service';

interface CampaignFollowFormProps {
  followRule?: CampaignFollow | null;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading?: boolean;
  onClose?: () => void;
}

export default function CampaignFollowForm({
  followRule,
  onSubmit,
  isLoading,
  onClose,
}: CampaignFollowFormProps) {
  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns-list'],
    queryFn: () => campaignService.getAll(1, 1000, { active_only: true }),
  });

  const campaigns = campaignsData?.data || [];

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <FormField>
        <FormLabel htmlFor="campaign_id" required>
          Campanha
        </FormLabel>
        <FormSelect
          id="campaign_id"
          name="campaign_id"
          required
          defaultValue={followRule?.campaign_id?.toString()}
        >
          <option value="">Selecione uma campanha</option>
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.name}
            </option>
          ))}
        </FormSelect>
      </FormField>

      <FormField>
        <FormLabel htmlFor="stage" required>
          Estágio Atual
        </FormLabel>
        <FormInput
          id="stage"
          name="stage"
          required
          defaultValue={followRule?.stage}
          maxLength={50}
          placeholder="Ex: lead_interessado"
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="interval_days" required>
          Intervalo (dias)
        </FormLabel>
        <FormInput
          id="interval_days"
          name="interval_days"
          type="number"
          required
          defaultValue={followRule?.interval_days}
          min={0}
          placeholder="Ex: 3"
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="new_stage" required>
          Novo Estágio
        </FormLabel>
        <FormInput
          id="new_stage"
          name="new_stage"
          required
          defaultValue={followRule?.new_stage}
          maxLength={50}
          placeholder="Ex: lead_sem_resposta"
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="new_action">
          Nova Ação
        </FormLabel>
        <FormInput
          id="new_action"
          name="new_action"
          defaultValue={followRule?.new_action || ''}
          maxLength={50}
          placeholder="Ex: enviar_email"
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="instructions">
          Instruções
        </FormLabel>
        <FormTextarea
          id="instructions"
          name="instructions"
          defaultValue={followRule?.instructions || ''}
          maxLength={255}
          rows={3}
          placeholder="Instruções para execução do follow-up"
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="message_delay_minutes">
          Atraso da Mensagem (minutos)
        </FormLabel>
        <FormInput
          id="message_delay_minutes"
          name="message_delay_minutes"
          type="number"
          defaultValue={followRule?.message_delay_minutes || 0}
          min={0}
          placeholder="Ex: 30"
        />
      </FormField>

      <FormField>
        <FormCheckbox
          label="Ativo"
          name="is_active"
          defaultChecked={followRule?.is_active !== false}
        />
      </FormField>

      <div className="flex justify-end gap-2 pt-4">
        {onClose && (
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Salvando...' : followRule ? 'Atualizar' : 'Criar'}
        </Button>
      </div>
    </form>
  );
}
